import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { DeltaBadge } from '@/components/dashboard/DeltaBadge';
import { SectionCard, StatusDot } from '@/components/section-card';
import { KpiStrip } from '@/components/kpi-strip';
import { usePostsQuery } from '@/lib/queries/posts';
import { useMessages } from '@/lib/queries/messages';
import { useChatStats } from '@/lib/queries/chat';
import { useTrafficStats } from '@/lib/queries/traffic';
import { useSearchStats } from '@/lib/queries/search';
import { useApFollowers } from '@/lib/queries/fediverse';
import { buildDashboardModel, type DashboardWorkItem, type DashboardTodayMetric } from '@/lib/admin-page-models';
import { TrafficSpark } from '@/components/dashboard/TrafficSpark';
import { EventFeed } from '@/components/dashboard/EventFeed';
import { ArrowUpRight, FileText, Inbox, Plus, SearchX } from 'lucide-react';

const TRAFFIC_DAYS = 14;

export const Route = createFileRoute('/')({ component: Dashboard });

function Dashboard() {
  const posts = usePostsQuery();
  const messages = useMessages(1, 5);
  const unreadMessages = useMessages(1, 20, 'unread');
  const chatStats = useChatStats(TRAFFIC_DAYS);
  const traffic = useTrafficStats(TRAFFIC_DAYS);
  const search = useSearchStats(TRAFFIC_DAYS);
  const followers = useApFollowers();

  const postCount = posts.data?.length ?? 0;
  const publishedCount = posts.data?.filter(p => p.status === 'published').length ?? 0;
  const draftCount = postCount - publishedCount;
  const dashboard = buildDashboardModel({
    posts: posts.data?.map(post => ({ status: post.status, createdAt: post.created_at })),
    unreadMessages: unreadMessages.data?.total,
    searchStats: search.data,
    trafficStats: traffic.data,
    chatStats: chatStats.data,
    chatDaily: chatStats.data?.daily,
    trafficDaily: traffic.data?.daily,
  });
  const { chatWeek, trafficWeek } = dashboard;

  return (
    <>
      <PageHeader
        title="대시보드"
        description="밖에서 무슨 일 있었나"
        actions={
          <Button asChild size="sm">
            <Link to="/posts/new"><Plus /> 새 글</Link>
          </Button>
        }
      />

      {/* 지표 밴드 — 전폭 상단 (주간값 + 델타) */}
      <KpiStrip
        className="fade-up"
        items={[
          {
            label: '글',
            value: postCount.toLocaleString('ko-KR'),
            hint: `발행 ${publishedCount} · 미발행 ${draftCount}`,
          },
          {
            label: '메시지',
            value: (messages.data?.total ?? 0).toLocaleString('ko-KR'),
            tone: (unreadMessages.data?.total ?? 0) > 0 ? 'warn' : 'default',
            hint: (unreadMessages.data?.total ?? 0) > 0 ? `안 읽음 ${unreadMessages.data?.total ?? 0}` : '모두 읽음',
          },
          {
            label: 'AI 세션',
            value: chatWeek.recent.toLocaleString('ko-KR'),
            tone: 'info',
            hint: <DeltaBadge current={chatWeek.recent} prior={chatWeek.prior} />,
          },
          {
            label: '페이지뷰',
            value: trafficWeek.recent.toLocaleString('ko-KR'),
            tone: 'sage',
            hint: <DeltaBadge current={trafficWeek.recent} prior={trafficWeek.prior} />,
          },
          {
            label: '페디버스 팔로워',
            value: (followers.data?.total ?? 0).toLocaleString('ko-KR'),
          },
        ]}
      />

      {/* 2×2 정렬 격자: 이벤트↔할일 / 오늘흐름↔트래픽 */}
      <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr] fade-up">
        <EventFeed />

        <SectionCard title="오늘 확인할 일" description="처리 우선순위" compact>
          <WorkList items={dashboard.workItems} />
        </SectionCard>

        <SectionCard
          title="오늘 흐름"
          description="실시간 기준"
          compact
          className="flex flex-col"
          bodyClassName="flex-1 flex items-center"
        >
          <TodayMetrics items={dashboard.todayMetrics} />
        </SectionCard>

        <TrafficSpark query={traffic} days={TRAFFIC_DAYS} density={dashboard.trafficTrendDensity} />
      </div>
    </>
  );
}

const workIcons = {
  messages: Inbox,
  drafts: FileText,
  'zero-search': SearchX,
} as const;

const workTone = {
  default: 'text-muted-foreground bg-muted',
  signal: 'text-signal bg-signal/10',
  info: 'text-info bg-info/10',
  warn: 'text-warn bg-warn/10',
} as const;

function WorkList({ items }: { items: DashboardWorkItem[] }) {
  return (
    <ul className="-my-2 divide-y divide-border/80">
      {items.map(item => {
        const Icon = workIcons[item.key];
        return (
          <li key={item.key}>
            <Link
              to={item.href}
              className="group -mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition-colors hover:bg-accent/45"
            >
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${workTone[item.tone]}`}>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-[13.5px] font-medium tracking-tight">{item.label}</span>
                  <StatusDot tone={item.tone === 'warn' ? 'warning' : item.tone === 'signal' ? 'signal' : 'muted'} />
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] text-muted-foreground">
                  {item.description}
                </span>
              </span>
              <span className="font-mono text-[18px] font-semibold tabular-nums text-foreground">
                {item.value}
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.75} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function TodayMetrics({ items }: { items: DashboardTodayMetric[] }) {
  return (
    <div className="grid w-full grid-cols-3">
      {items.map((item, i) => (
        <Link
          key={item.key}
          to={item.href}
          className={`group flex flex-col gap-2 rounded-md px-3 py-1 transition-colors hover:bg-accent/45 ${i > 0 ? 'border-l border-border/60' : ''}`}
        >
          <span className="truncate text-[12px] text-muted-foreground">{item.label}</span>
          <span className="font-mono text-[19px] font-semibold tabular-nums text-foreground">{item.value}</span>
        </Link>
      ))}
    </div>
  );
}
